import type {
  IncidentStatus,
  ManualTimelineType,
  TimelineEventResponse,
} from "./api";

/** Display order of the status lifecycle (GR-001). */
export const STATUS_ORDER: readonly IncidentStatus[] = [
  "neu",
  "in_analyse",
  "in_bearbeitung",
  "beobachtet",
  "geloest",
  "abgeschlossen",
];

/**
 * Forward transition map mirroring the backend GR-001 graph. The server remains
 * the authority; this drives which options the status control offers.
 */
export const NEXT_STATUSES: Record<IncidentStatus, readonly IncidentStatus[]> = {
  neu: ["in_analyse", "in_bearbeitung"],
  in_analyse: ["in_bearbeitung", "beobachtet", "geloest"],
  in_bearbeitung: ["in_analyse", "beobachtet", "geloest"],
  beobachtet: ["in_bearbeitung", "geloest"],
  geloest: ["in_bearbeitung", "abgeschlossen"],
  // Terminal for the generic status PATCH; leaving abgeschlossen goes through
  // the dedicated reopen action (F-018/GR-007), not the status Select.
  abgeschlossen: [],
};

/**
 * The status options the control offers for this incident. DO-001 requires
 * the Incident Owner for every status past "neu", so without one there is no
 * status change to offer - the missing-owner callout says why (D-004).
 */
export function availableNextStatuses(
  status: IncidentStatus,
  ownerId: string | null,
): readonly IncidentStatus[] {
  return ownerId ? NEXT_STATUSES[status] : [];
}

/**
 * D-005: a status-change entry written before the fix says "Status von
 * in_analyse auf geloest geaendert." - slugs, not labels. Timeline rows are
 * immutable, so old entries are relabelled here; new ones already carry the
 * labels and pass through. Only the exact template and only real slugs.
 */
export function timelineText(
  event: Pick<TimelineEventResponse, "type" | "text">,
  t: (key: string) => string,
): string | null {
  if (!event.text || event.type !== "statuswechsel") return event.text;
  const match = /^Status von (\S+) auf (\S+) geändert\.$/.exec(event.text);
  if (!match) return event.text;
  const show = (token: string): string =>
    (STATUS_ORDER as readonly string[]).includes(token)
      ? t(STATUS_LABEL_KEY[token as IncidentStatus])
      : token;
  return `Status von ${show(match[1] ?? "")} auf ${show(match[2] ?? "")} geändert.`;
}

/** The three manual timeline entry types a writer may add (GR-003). */
export const MANUAL_TIMELINE_TYPES: readonly ManualTimelineType[] = [
  "kommentar",
  "massnahme",
  "externe_auswirkung",
];

// i18n key maps. Callers resolve these via a variable (t(labelKey)), never via a
// string-literal argument, so the check-i18n gate never sees a partial key; every
// value below exists in de.ts.
export const STATUS_LABEL_KEY: Record<IncidentStatus, string> = {
  neu: "incidentStatus.neu",
  in_analyse: "incidentStatus.in_analyse",
  in_bearbeitung: "incidentStatus.in_bearbeitung",
  beobachtet: "incidentStatus.beobachtet",
  geloest: "incidentStatus.geloest",
  abgeschlossen: "incidentStatus.abgeschlossen",
};

export const TIMELINE_TYPE_LABEL_KEY: Record<
  TimelineEventResponse["type"],
  string
> = {
  kommentar: "timelineType.kommentar",
  massnahme: "timelineType.massnahme",
  externe_auswirkung: "timelineType.externe_auswirkung",
  statuswechsel: "timelineType.statuswechsel",
  rollenaenderung: "timelineType.rollenaenderung",
  tag_aenderung: "timelineType.tag_aenderung",
  anhang_hinzugefuegt: "timelineType.anhang_hinzugefuegt",
  anhang_entfernt: "timelineType.anhang_entfernt",
  postmortem_erstellt: "timelineType.postmortem_erstellt",
  wiedereroeffnung: "timelineType.wiedereroeffnung",
  erinnerung: "timelineType.erinnerung",
};

// Functional status colours, deliberately distinct from the Ryddion brand
// accent (orange), and never the only signal (the label always shows too).
export const STATUS_BADGE_CLASS: Record<IncidentStatus, string> = {
  neu: "border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-900/40 text-slate-800 dark:text-slate-200",
  in_analyse: "border-sky-300 dark:border-sky-700 bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200",
  in_bearbeitung: "border-amber-300 dark:border-amber-700 bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100",
  beobachtet: "border-violet-300 dark:border-violet-700 bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200",
  geloest: "border-emerald-300 dark:border-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200",
  abgeschlossen: "border-slate-300 dark:border-slate-700 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
};

// Matching dot colour per status (K6): a small solid dot inside the pill, one
// shade deeper than the pill fill. The dot supports the label; it is never the
// only signal (the German status text always shows too).
export const STATUS_DOT_CLASS: Record<IncidentStatus, string> = {
  neu: "bg-slate-500",
  in_analyse: "bg-sky-500",
  in_bearbeitung: "bg-amber-500",
  beobachtet: "bg-violet-500",
  geloest: "bg-emerald-500",
  abgeschlossen: "bg-slate-400",
};

/** True for the manual entry types (used to distinguish system events). */
export function isManualTimelineType(
  type: TimelineEventResponse["type"],
): type is ManualTimelineType {
  return (
    type === "kommentar" ||
    type === "massnahme" ||
    type === "externe_auswirkung"
  );
}

/** Client-side timeline view filter (M-005): all / manual / system events. */
export type TimelineFilter = "alle" | "manuell" | "system";

export const TIMELINE_FILTERS: readonly TimelineFilter[] = [
  "alle",
  "manuell",
  "system",
];

export const TIMELINE_FILTER_LABEL_KEY: Record<TimelineFilter, string> = {
  alle: "detail.timelineFilterAll",
  manuell: "detail.timelineFilterManual",
  system: "detail.timelineFilterSystem",
};

/** True when an event of the given type passes the segmented filter. */
export function matchesTimelineFilter(
  filter: TimelineFilter,
  type: TimelineEventResponse["type"],
): boolean {
  if (filter === "alle") return true;
  return filter === "manuell"
    ? isManualTimelineType(type)
    : !isManualTimelineType(type);
}
